export type FunnelUser = {
  uid: string;
  name: string;
  email: string;
};

/**
 * The three engagement groups asked for in #52, computed for every registered
 * user:
 *   • neither      — signed up but never created and never joined a conversation
 *   • created      — created at least one conversation of their own
 *   • joinedOthers — signed up for at least one conversation someone else created
 *
 * `created` and `joinedOthers` can overlap (a host who also joins other rooms
 * appears in both). `neither` is the exclusive remainder — a user who is in
 * neither of the other two groups.
 *
 * "Joined" is measured from room.participants (signing up for a room), the same
 * signal the attendance screen calls "signed up". That is the engagement event
 * this funnel is about; it needs no per-room presence read.
 */
export type SignupFunnel = {
  total: number;
  neither: FunnelUser[];
  created: FunnelUser[];
  joinedOthers: FunnelUser[];
};

export type FunnelInputUser = { uid: string; name?: string; email?: string };
export type FunnelInputRoom = {
  createdBy?: string;
  participants?: string[];
};

const byName = (a: FunnelUser, b: FunnelUser): number =>
  a.name.localeCompare(b.name);

/**
 * Pure grouping logic, split out from the Firestore read so it can be tested
 * without a database. Users are the roster; rooms carry the creator
 * (`createdBy`) and the sign-ups (`participants`).
 */
export function computeSignupFunnel(
  users: FunnelInputUser[],
  rooms: FunnelInputRoom[],
): SignupFunnel {
  const creatorUids = new Set<string>();
  const joinedOthersUids = new Set<string>();

  rooms.forEach((room) => {
    const createdBy =
      typeof room.createdBy === "string" ? room.createdBy : undefined;
    if (createdBy) {
      creatorUids.add(createdBy);
    }

    const participants = Array.isArray(room.participants)
      ? room.participants.filter(
          (uid): uid is string => typeof uid === "string",
        )
      : [];
    participants.forEach((uid) => {
      // Signing up for your own room is not "joining someone else's".
      if (uid !== createdBy) {
        joinedOthersUids.add(uid);
      }
    });
  });

  const toFunnelUser = (user: FunnelInputUser): FunnelUser => ({
    uid: user.uid,
    name: user.name?.trim() || "Unknown user",
    email: user.email?.trim() || "",
  });

  const neither: FunnelUser[] = [];
  const created: FunnelUser[] = [];
  const joinedOthers: FunnelUser[] = [];

  users.forEach((user) => {
    const didCreate = creatorUids.has(user.uid);
    const didJoinOthers = joinedOthersUids.has(user.uid);
    const funnelUser = toFunnelUser(user);
    if (didCreate) {
      created.push(funnelUser);
    }
    if (didJoinOthers) {
      joinedOthers.push(funnelUser);
    }
    if (!didCreate && !didJoinOthers) {
      neither.push(funnelUser);
    }
  });

  return {
    total: users.length,
    neither: neither.sort(byName),
    created: created.sort(byName),
    joinedOthers: joinedOthers.sort(byName),
  };
}
